import { compact, isEmpty } from 'lodash';

import measurementsRegistry from '../measurementsStore/measurementsRegistry';
import { syncedMeasurements } from '../measurementsStore/elementMeasurements/syncedMeasurementsSingleton';
import { getCurrentUser } from '../../user/currentUserSelector';
import getGridSize from '../../utils/grid/gridSizeSelector';
import { getCurrentVisibleBoardCanvasOrigin } from '../../element/selectors/currentBoardSelector';
import { getIsRedoing, getIsUndoing, getMostRecentTransactionId } from '../../utils/undoRedo/undoRedoSelector';
import { selectIsResizeInProgress } from '../../element/resizing/store/resizingSelector';
import { moveMultipleElements } from '../../element/actions/elementMoveActions';
import { prop } from '../../../common/utils/immutableHelper';
import { ELEMENT_MOVE_OPERATIONS } from '../../../common/elements/elementConstants';
import { ElementMeasurement } from '../measurementsStore/elementMeasurements/elementMeasurementsTypes';
import { ActionMove } from '../../../common/actions/actionTypes';
import { ReduxState, Thunk } from '../../types/reduxTypes';
import {
    blockCollisionDetectionUntilNavigation,
    clearPendingBatchCollision,
    collisionDetectionManager,
    getPendingBatchCollision,
    isPendingBatchCollisionElementId,
} from './collisionDetectionManager';
import {
    clearCollisionCount,
    incrementCollisionMoveCount,
    isReadyToCheckCollisions,
    MAX_COLLISION_MOVE_COUNT,
    recursivelyCheckCollisions,
    removeDuplicateMoves,
} from './elementCollisionActions';

const MAX_BATCH_COLLISION_ELEMENT_COUNT = 50;

type PreviousMeasurementsById = Record<string, ElementMeasurement | undefined>;

type BatchCollisionContext = {
    currentUser: ReturnType<typeof getCurrentUser>;
    elementIds: string[];
    previousMeasurementsById: PreviousMeasurementsById;
    updatedMeasurementsMap: Record<string, ElementMeasurement | undefined>;
    gridSize: number;
    canvasOriginY: number;
};

type RootCollisionDetail = {
    elementId: string;
    nextMeasurement: ElementMeasurement;
};

type CollisionMove = ActionMove & {
    autoPush?: boolean;
    noSync?: boolean;
    saveOriginalLocation?: boolean;
};

const getUpdatedMeasurementAfterMove = (
    measurement: ElementMeasurement | undefined,
    move: CollisionMove,
    gridSize: number,
    canvasOriginY: number,
): ElementMeasurement | undefined => {
    if (!measurement || !move?.location?.position) return measurement;

    const positionY = Number(move.location.position.y);
    if (Number.isNaN(positionY)) return measurement;

    const nextTop = (positionY - canvasOriginY) * gridSize;

    return {
        ...measurement,
        top: nextTop,
        y: nextTop,
        bottom: nextTop + measurement.height,
    };
};

const applyMovesToMeasurementsMap = (
    moves: CollisionMove[],
    measurementsMap: Record<string, ElementMeasurement | undefined>,
    gridSize: number,
    canvasOriginY: number,
) => {
    moves.forEach((move) => {
        const currentMeasurement = measurementsMap[move.id];
        if (!currentMeasurement) return;

        measurementsMap[move.id] = getUpdatedMeasurementAfterMove(currentMeasurement, move, gridSize, canvasOriginY);
    });
};

const getBatchCollisionContext = (state: ReduxState): BatchCollisionContext | null => {
    const pendingBatchCollision = getPendingBatchCollision();
    const elementIds = pendingBatchCollision?.elementIds || [];
    const previousMeasurementsById = pendingBatchCollision?.previousMeasurements || {};

    if (isEmpty(elementIds)) return null;
    if (elementIds.length > MAX_BATCH_COLLISION_ELEMENT_COUNT) return null;

    const currentUser = getCurrentUser(state);
    const isResizing = selectIsResizeInProgress(state);
    const isUndoing = getIsUndoing(state);
    const isRedoing = getIsRedoing(state);

    if (isResizing || isUndoing || isRedoing) return null;

    const canvasOrigin = getCurrentVisibleBoardCanvasOrigin(state);
    const canvasOriginY = prop('y', canvasOrigin);
    const gridSize = getGridSize(state);
    const updatedMeasurementsMap = { ...syncedMeasurements };

    return {
        currentUser,
        elementIds,
        previousMeasurementsById,
        updatedMeasurementsMap,
        gridSize,
        canvasOriginY,
    };
};

const getBatchCollisionRootDetails = ({
    elementIds,
    previousMeasurementsById,
    updatedMeasurementsMap,
}: BatchCollisionContext): RootCollisionDetail[] =>
    compact(
        elementIds.map((elementId) => {
            if (!isPendingBatchCollisionElementId(elementId)) return null;

            const previousMeasurement = previousMeasurementsById[elementId];
            const nextMeasurement = measurementsRegistry.getElementMeasurementFromCanvasDocument(elementId);

            if (!previousMeasurement || !nextMeasurement) return null;

            updatedMeasurementsMap[elementId] = nextMeasurement;

            return {
                elementId,
                nextMeasurement,
            };
        }),
    );

const getBatchCollisionMoves = ({
    rootCollisionDetails,
    state,
    updatedMeasurementsMap,
    currentUser,
    previousMeasurementsById,
    gridSize,
    canvasOriginY,
}: BatchCollisionContext & { rootCollisionDetails: RootCollisionDetail[]; state: ReduxState }): CollisionMove[] =>
    rootCollisionDetails
        .sort((detailA, detailB) => detailA.nextMeasurement.top - detailB.nextMeasurement.top)
        .map(({ elementId }) => {
            const rootMoves =
                recursivelyCheckCollisions(
                    elementId,
                    updatedMeasurementsMap[elementId],
                    false,
                    state,
                    updatedMeasurementsMap,
                    currentUser,
                    previousMeasurementsById,
                ) || [];

            applyMovesToMeasurementsMap(rootMoves as CollisionMove[], updatedMeasurementsMap, gridSize, canvasOriginY);
            return rootMoves as CollisionMove[];
        })
        .flat();

const getBatchCollisionMoveAction = (state: ReduxState): ReturnType<typeof moveMultipleElements> | null => {
    if (collisionDetectionManager.blocked) return null;
    if (!isReadyToCheckCollisions(state)) return null;

    const batchCollisionContext = getBatchCollisionContext(state);
    if (!batchCollisionContext) return null;

    const rootCollisionDetails = getBatchCollisionRootDetails(batchCollisionContext);
    if (isEmpty(rootCollisionDetails)) return null;

    const moves = getBatchCollisionMoves({
        rootCollisionDetails,
        state,
        ...batchCollisionContext,
    });
    if (isEmpty(moves)) return null;

    if (collisionDetectionManager.blocked) return null;

    if (incrementCollisionMoveCount() >= MAX_COLLISION_MOVE_COUNT) {
        blockCollisionDetectionUntilNavigation();
        return null;
    }

    clearCollisionCount();

    const dedupedMoves = removeDuplicateMoves(moves) as CollisionMove[];
    const mostRecentTransactionId = getMostRecentTransactionId(state);
    const shouldSync = dedupedMoves.every((move) => !move.noSync);

    return moveMultipleElements({
        moves: dedupedMoves,
        transactionId: mostRecentTransactionId,
        sync: shouldSync,
        moveOperation: ELEMENT_MOVE_OPERATIONS.COLLISION,
        initialMeasurements: undefined,
        actionOrigin: undefined,
    });
};

export const checkBatchCollisions = (): Thunk => (dispatch, getState) => {
    const state = getState();
    const batchCollisionAction = getBatchCollisionMoveAction(state);

    clearPendingBatchCollision();

    if (!batchCollisionAction) return;
    dispatch(batchCollisionAction);
};
